/**
 * Gewohnheiten-Repository.
 *
 * Eintraege haben eine deterministische Kennung `${habitId}:${date}`. Dadurch
 * ist das Abhaken ein simples `put` - es kann keine zwei Eintraege fuer
 * denselben Tag geben, auch nicht bei einem Doppelklick oder in zwei Tabs.
 */
import { db } from '@/db/schema';
import { inDenPapierkorb } from '@/db/repositories/papierkorb';
import { nowInstant } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';
import type { Habit, HabitCadence, HabitEntry, ID, LocalDate } from '@/domain/types';

export type HabitDraft = Pick<Habit, 'name' | 'cadence'>
  & Partial<Pick<Habit, 'areaId' | 'targetValue' | 'unit' | 'routine' | 'order'>>;

export function entryId(habitId: ID, date: LocalDate): ID {
  return `${habitId}:${date}`;
}

export async function createHabit(draft: HabitDraft): Promise<Habit> {
  const ts = nowInstant();
  const maxOrder = (await db.habits.orderBy('order').last())?.order ?? -1;

  const habit: Habit = {
    id: newId(),
    areaId: draft.areaId ?? null,
    name: draft.name.trim(),
    cadence: draft.cadence,
    targetValue: draft.targetValue,
    unit: draft.unit,
    routine: draft.routine ?? null,
    order: draft.order ?? maxOrder + 1,
    archived: false,
    createdAt: ts,
    updatedAt: ts,
  };

  await db.habits.add(habit);
  return habit;
}

export async function updateHabit(
  id: ID,
  patch: Partial<Omit<Habit, 'id' | 'createdAt'>>,
): Promise<void> {
  await db.habits.update(id, { ...patch, updatedAt: nowInstant() });
}

export async function archiveHabit(id: ID): Promise<void> {
  await updateHabit(id, { archived: true });
}

export async function unarchiveHabit(id: ID): Promise<void> {
  await updateHabit(id, { archived: false });
}

/** Loescht die Gewohnheit samt ihrer Verlaufsdaten - 30 Tage im Papierkorb (F-45). */
export async function deleteHabit(id: ID): Promise<void> {
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'habits', zeileIds: [id] }, () => db.transaction('rw', db.habits, db.habitEntries, async () => {
    await db.habitEntries.where('habitId').equals(id).delete();
    await db.habits.delete(id);
  }));
}

/**
 * Abhaken oder zuruecknehmen.
 *
 * Gibt den neuen Zustand zurueck, damit die Oberflaeche nicht nachladen muss.
 */
export async function toggleHabitDone(
  habitId: ID,
  date: LocalDate,
  value?: number,
): Promise<boolean> {
  return db.transaction('rw', db.habitEntries, async () => {
    const id = entryId(habitId, date);
    const existing = await db.habitEntries.get(id);
    const next = !existing?.done;

    const entry: HabitEntry = {
      id,
      habitId,
      date,
      done: next,
      value: next ? (value ?? existing?.value) : existing?.value,
      note: existing?.note,
      updatedAt: nowInstant(),
    };

    await db.habitEntries.put(entry);
    return next;
  });
}

/** Alle erledigten Tage einer Gewohnheit in einem Zeitraum. */
export async function doneDatesInRange(
  habitId: ID,
  from: LocalDate,
  to: LocalDate,
): Promise<Set<LocalDate>> {
  const rows = await db.habitEntries
    .where('[habitId+date]')
    .between([habitId, from], [habitId, to], true, true)
    .toArray();

  return new Set(rows.filter((r) => r.done).map((r) => r.date));
}

/** Reihenfolge neu setzen - `ids` in der gewuenschten Sortierung. */
export async function reorderHabits(ids: ID[]): Promise<void> {
  const ts = nowInstant();
  await db.transaction('rw', db.habits, async () => {
    await Promise.all(ids.map((id, order) => db.habits.update(id, { order, updatedAt: ts })));
  });
}

/** Lesbare Beschreibung eines Rhythmus - fuer Listen und Formulare. */
export function describeCadence(cadence: HabitCadence): string {
  const NAMEN = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

  switch (cadence.type) {
    case 'taeglich':
      return 'Täglich';
    case 'wochentage': {
      const tage = [...cadence.days].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7));
      if (tage.length === 7) return 'Täglich';
      if (tage.length === 5 && [1, 2, 3, 4, 5].every((d) => tage.includes(d))) {
        return 'Werktags';
      }
      if (tage.length === 2 && tage.includes(0) && tage.includes(6)) return 'Am Wochenende';
      return tage.map((d) => NAMEN[d]).join(', ');
    }
    case 'x_pro_woche':
      return cadence.count === 1 ? 'Einmal pro Woche' : `${cadence.count}× pro Woche`;
  }
}
